import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class SquarespaceSettingsReadOnly extends ModelObject<SquarespaceSettingsReadOnly> {

    /**
    * The Squarespace webhook id if an order.create webhook has been registered for this tenant. This id is needed to unregister.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public webhookOrderCreateId: string = "";
    /**
    * The Squarespace webhook id if an order.update webhook has been registered for this tenant. This id is needed to unregister.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public webhookOrderUpdateId: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.webhookOrderCreateId = jsonSerializedObject.webhookOrderCreateId;
          this.webhookOrderUpdateId = jsonSerializedObject.webhookOrderUpdateId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.webhookOrderCreateId = this.webhookOrderCreateId;
        toRet.webhookOrderUpdateId = this.webhookOrderUpdateId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SquarespaceSettings extends ModelObject<SquarespaceSettings> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _defaultDeliveryStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for delivery charge of imported orders. This will be used if no BrewMan product name matches the Shipping Line title in Squarespace
    */
    public get defaultDeliveryStockItemId(): Guid | null { return this._defaultDeliveryStockItemId }
    public set defaultDeliveryStockItemId(value: Guid | null) {
      this._defaultDeliveryStockItemId = value === undefined ? null : value;
    }
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on Squarespace from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet Id for all Squarespace orders import to a single outlet. Should be null only if tenant doesn't use Squarespace.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _priceListId: Guid | null = null;
    /**
    * PriceList Id. If provided then Products on Squarespace will be updated with the List Price in BrewMan.
    */
    public get priceListId(): Guid | null { return this._priceListId }
    public set priceListId(value: Guid | null) {
      this._priceListId = value === undefined ? null : value;
    }
    private _stockLocationId: Guid | null = null;
    /**
    * StockLocation Id. If provided then product on Squarespace will be updated with the Stock Location's stock level in BrewMan.
    */
    public get stockLocationId(): Guid | null { return this._stockLocationId }
    public set stockLocationId(value: Guid | null) {
      this._stockLocationId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    public readOnly: SquarespaceSettingsReadOnly = new SquarespaceSettingsReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.defaultDeliveryStockItemId = jsonSerializedObject.defaultDeliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDeliveryStockItemId);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.priceListId = jsonSerializedObject.priceListId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.stockLocationId = jsonSerializedObject.stockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.readOnly = new SquarespaceSettingsReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.defaultDeliveryStockItemId = this.defaultDeliveryStockItemId == null ? null : this.defaultDeliveryStockItemId.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.priceListId = this.priceListId == null ? null : this.priceListId.toString();
        toRet.stockLocationId = this.stockLocationId == null ? null : this.stockLocationId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "SquarespaceSettings.Id cannot be empty"},
            {field: "defaultDeliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "SquarespaceSettings.DefaultDeliveryStockItemId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "SquarespaceSettings.OutletId cannot be empty"},
            {field: "priceListId", rule: (v: any) => v == null || !v.isEmpty() || "SquarespaceSettings.PriceListId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "SquarespaceSettings.StockLocationId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class TenantSettingsId extends ModelObject<TenantSettingsId> {

    public tenantId: Guid = Guid.createEmpty();
    public squarespaceSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.squarespaceSettingsId = Guid.parseOrEmpty(jsonSerializedObject.squarespaceSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.squarespaceSettingsId = this.squarespaceSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.TenantId cannot be empty"},
            {field: "squarespaceSettingsId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.SquarespaceSettingsId cannot be empty"},
    ];
}


export class SquarespaceSettingsService extends ServiceBase {

  public async getSquarespaceSettings(squarespaceSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SquarespaceSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    squarespaceSettingsId:squarespaceSettingsId.toString(),
    };
    const extractor = (response:any) => new SquarespaceSettings(response.squarespaceSettings);;
    return this.makeJsonRequest("squarespaceSettings/v1/getSquarespaceSettings", req, false, extractor, opts);
  };

  public async saveSquarespaceSettings(squarespaceSettings: SquarespaceSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    squarespaceSettings:squarespaceSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("squarespaceSettings/v1/saveSquarespaceSettings", req, true, extractor, opts);
  };

  public async hardDeleteSquarespaceSettings(squarespaceSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    squarespaceSettingsId:squarespaceSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("squarespaceSettings/v1/hardDeleteSquarespaceSettings", req, true, extractor, opts);
  };

  public async createSquarespaceLoginUrl(squarespaceSettingsId: Guid, returnPageUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    squarespaceSettingsId:squarespaceSettingsId.toString(),
    returnPageUrl:returnPageUrl,
    };
    const extractor = (response:any) => response.url;;
    return this.makeJsonRequest("squarespaceSettings/v1/createSquarespaceLoginUrl", req, false, extractor, opts);
  };

  public async completeSquarespaceAuthentication(squarespaceSettingsId: Guid, queryString: string, returnPageUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SquarespaceSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    squarespaceSettingsId:squarespaceSettingsId.toString(),
    queryString:queryString,
    returnPageUrl:returnPageUrl,
    };
    const extractor = (response:any) => new SquarespaceSettings(response.squarespaceSettings);;
    return this.makeJsonRequest("squarespaceSettings/v1/completeSquarespaceAuthentication", req, true, extractor, opts);
  };

  public async isSquarespaceAuthenticated(squarespaceSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:api.tenantId.toString(),
    squarespaceSettingsId:squarespaceSettingsId.toString(),
    };
    const extractor = (response:any) => response.authenticated;;
    return this.makeJsonRequest("squarespaceSettings/v1/isSquarespaceAuthenticated", req, false, extractor, opts);
  };

  public async removeSquarespaceAuthentication(squarespaceSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    squarespaceSettingsId:squarespaceSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("squarespaceSettings/v1/removeSquarespaceAuthentication", req, true, extractor, opts);
  };

  public async getSquarespaceSettingsByTenant(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SquarespaceSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new SquarespaceSettings(response.squarespaceSettings);;
    return this.makeJsonRequest("squarespaceSettings/v1/getSquarespaceSettingsByTenant", req, false, extractor, opts);
  };
}

export const squarespaceSettingsService = new SquarespaceSettingsService();